
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function ResidentialElectricalSafetyInspectionsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Residential Electrical Safety Inspections',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Comprehensive residential electrical safety inspections in Fresno, CA. Identify hazards, code violations, and ensure your home electrical system is safe and compliant.',
    url: `${siteUrl}/services/testing-certification/residential-electrical-safety-inspections`,
  };

  const features = [
    {
      icon: 'ri-home-4-line',
      title: 'Complete Home Assessment',
      description:
        'Thorough inspection of all electrical systems, panels, circuits, and components throughout your Fresno home',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Safety Hazard Detection',
      description:
        'Identify fire risks, shock hazards, outdated wiring, and dangerous electrical conditions',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Code Compliance Review',
      description:
        'Verify your electrical system meets California Title 24 and NEC standards',
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Thermal Imaging Scans',
      description:
        'Infrared technology detects hot spots, overloaded circuits, and hidden electrical problems',
    },
    {
      icon: 'ri-tools-line',
      title: 'Panel & Breaker Testing',
      description:
        'Test electrical panels, circuit breakers, GFCI/AFCI devices, and grounding systems',
    },
    {
      icon: 'ri-file-text-line',
      title: 'Detailed Written Report',
      description:
        'Comprehensive inspection report with photos, findings, and safety recommendations',
    },
    {
      icon: 'ri-home-gear-line',
      title: 'Outlet & Switch Testing',
      description:
        'Check all outlets, switches, and receptacles for proper wiring and functionality',
    },
    {
      icon: 'ri-alert-line',
      title: 'Priority Issue Identification',
      description:
        'Clear categorization of urgent safety issues vs. recommended improvements',
    },
  ];

  const whatsIncluded = [
    'Licensed C-10 electrician inspection',
    'Electrical panel and breaker assessment',
    'Circuit load and capacity testing',
    'GFCI/AFCI protection verification',
    'Grounding and bonding inspection',
    'Outlet and switch functionality testing',
    'Thermal imaging scans (when applicable)',
    'Detailed written report with photos',
    'Safety recommendations and estimates',
    'Code compliance documentation',
  ];

  return (
    <>
      <SEOHead
        title="Residential Electrical Safety Inspections Fresno CA | Home Electrical Testing"
        description="Professional residential electrical safety inspections in Fresno, CA. Licensed electricians identify hazards, code violations, and ensure your home is safe. Detailed reports included."
        canonical="/services/testing-certification/residential-electrical-safety-inspections"
        keywords="residential electrical inspection Fresno, home electrical safety inspection, electrical testing Fresno, electrical safety check"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Residential Electrical Safety Inspections — Fresno, CA"
        heroSubtitle="Comprehensive home electrical inspections to protect your family and property"
        heroImage="https://public.readdy.ai/ai/img_res/2f910584bc4ef2b65fd00ab67fb0cb70.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20electrical%20safety%20inspection%20equipment%20including%20digital%20multimeter%20thermal%20camera%20and%20clipboard%20with%20inspection%20checklist%20on%20table%20next%20to%20residential%20electrical%20panel%20in%20bright%20California%20home%20interior&width=700&height=600&seq=res-safety-fresno-001&orientation=portrait"
        fresnoContent="Fresno homeowners rely on our thorough residential electrical safety inspections to identify hazards before they become dangerous. Whether you're buying a home in Woodward Park, concerned about an older property in Tower District, or simply want peace of mind about your electrical system, our licensed C-10 electricians provide comprehensive inspections that meet California safety standards. We check every panel, circuit, outlet, and wire to ensure your family is protected."
        features={features}
        whatsIncluded={whatsIncluded}
        faqs={[
          {
            question: 'What does a residential electrical safety inspection in Fresno include?',
            answer:
              'Our Fresno residential inspections cover the electrical panel, circuit breakers, wiring, grounding systems, outlets, switches, GFCI/AFCI protection, lighting fixtures, and overall code compliance. We use thermal imaging to detect hot spots and test circuits for proper load capacity. You receive a detailed written report with photos, findings, and safety recommendations for your Fresno home.',
          },
          {
            question: 'How much does a home electrical safety inspection cost in Fresno?',
            answer:
              'Residential electrical safety inspections in Fresno typically range from $200–$400 depending on home size and system complexity. Larger homes or properties with multiple panels may cost more. We provide upfront pricing and include a comprehensive written report with all inspections. Thermal imaging and specialized testing may be additional.',
          },
          {
            question: 'How often should I get an electrical safety inspection for my Fresno home?',
            answer:
              'We recommend electrical safety inspections every 3–5 years for newer homes, and every 1–3 years for homes over 25 years old. You should also get an inspection before buying a home, after major renovations, if you notice flickering lights or tripped breakers, or if your Fresno home has outdated wiring like knob-and-tube or aluminum.',
          },
          {
            question: 'Are your electrical inspections accepted by Fresno building departments and insurance companies?',
            answer:
              'Yes. Our electricians are licensed by the California Contractors State License Board (CSLB) with C-10 licenses, and our inspection reports meet all requirements for Fresno building permits, insurance claims, real estate transactions, and code compliance documentation throughout Fresno County.',
          },
          {
            question: 'What are common electrical safety issues found in Fresno homes?',
            answer:
              "Common issues we find in Fresno homes include outdated electrical panels, insufficient GFCI/AFCI protection, overloaded circuits, improper grounding, aluminum wiring, knob-and-tube wiring, DIY electrical work not up to code, and panels that don't meet current California Title 24 standards. Our inspections identify these hazards before they cause fires or injuries.",
          },
          {
            question: 'How long does a residential electrical safety inspection take in Fresno?',
            answer:
              'Most residential inspections take 1–3 hours depending on home size and electrical system complexity. Larger homes or properties with multiple panels, sub-panels, or outbuildings may take longer. We schedule inspections at your convenience and provide detailed reports within 24–48 hours for Fresno homeowners.',
          },
        ]}
      />
    </>
  );
}
